import React, { useEffect, useRef, useState } from 'react';

import { c } from 'ttag';

import { AuthenticationStore, Button, Modal, SkeletonLoader, useAuthentication } from '@freelancelabs/components';

import { recordMfaEnrolment } from '../../api/mfaEnrolment';
import PublicLayout from '../../app/content/PublicLayout';
import TotpPairing, { TOTP_CODE_LENGTH } from '../../app/mfa/TotpPairing';

interface Props {
    /**
     * What names the entry inside the person's authenticator app, their address.
     * Empty when the profile call did not carry one: the issuer alone then, which is
     * ugly but honest, rather than a name invented for them.
     */
    accountName: string;
    /** Where to go once a factor is registered. Called, never rendered as a link. */
    onDone: () => void;
}

/**
 * The screen a person meets when one of their accounts requires multi-factor
 * authentication and they carry no factor yet (PCG-303).
 *
 * **Attention, THIS IS A SCREEN AND NOT A MODAL, and the difference is the whole point.**
 * The profile page already sets up an authenticator app, in a modal with a « Annuler »
 * button, because there it is a choice. Here it is a condition of entry: a modal
 * invites dismissal, and a dismissable barrier is not one. There is therefore no
 * cancel, no close, and no way onward that does not go through a verified code.
 *
 * **What it does NOT do is leave the person stuck.** « Se déconnecter » stays available,
 * because the alternative to setting up a factor is to leave, not to be trapped. And
 * the failure of the setup call is said rather than swallowed: somebody who cannot see
 * a QR code has nothing to scan, and « rien ne se passe » is the worst of the states.
 *
 * **It reuses the store's `setupTotp` / `verifyTotpSetup`**, the very calls the profile
 * page makes, so there is one enrolment path in the application rather than two that
 * would drift. What differs is only what surrounds them.
 */
const MfaSetupPage = ({ accountName, onDone }: Props) => {
    const { setupTotp, verifyTotpSetup, logout, getAuthorizationToken } = useAuthentication() as AuthenticationStore;

    const [setup, setSetup] = useState<{ secret: string; uri: string } | null>(null);
    const [code, setCode] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    /**
     * The setup call failed: there is nothing to scan, and saying so is the only help.
     *
     * **The message is KEPT, not swallowed**, and that is the repository's own rule for a
     * refusal the screen cannot name: the sentence underneath is what the support desk
     * needs, and « ça n'a pas démarré » alone leaves the person with nothing to report.
     * Discovered the hard way on the first local run, where the screen said exactly that
     * and neither of us could tell why.
     */
    const [broken, setBroken] = useState<string | null>(null);

    /**
     * **UN APPEL DISTANT NON IDEMPOTENT DANS UN EFFET DE MONTAGE : LA PROMESSE SE
     * PARTAGE, ELLE NE SE GARDE PAS PAR UN DRAPEAU.** Ce lot a paye les deux erreurs,
     * l'une apres l'autre.
     *
     * `React.StrictMode` monte deux fois en developpement. Sans rien, `setUpTOTP`
     * partait donc deux fois et Cognito refusait : « ConcurrentModificationException:
     * Only one request to update user MFA settings can be processed at a time ». Un
     * echec affiche sur un appel parfaitement legitime.
     *
     * Le reflexe suivant, un `useRef` booleen qui saute le second passage, est PIRE et
     * bloque l'ecran : le premier passage est nettoye (son drapeau de vivacite retombe),
     * le second est saute, donc plus personne ne pose l'etat et le chargement tourne
     * indefiniment. C'est exactement ce qui est arrive.
     *
     * Ce qui marche est de retenir la PROMESSE : un seul appel distant, et chaque
     * passage s'y raccroche, donc chacun sait poser l'etat. Aucun drapeau de vivacite
     * n'est necessaire, et il serait meme nuisible ici pour la raison ci-dessus ; poser
     * l'etat d'un composant demonte est sans consequence depuis React 18.
     *
     * La page de profil n'a jamais rencontre tout cela parce qu'un CLIC y declenche la
     * pose. Le demarrage automatique reste juste sur une barriere, la personne etant
     * deja interrompue et le QR code etant tout l'objet de l'ecran ; c'est lui qui
     * impose ce motif.
     */
    const promesse = useRef<Promise<{ secret: string; uri: string }> | null>(null);

    useEffect(() => {
        if (!promesse.current) {
            // Le libelle que montre l'application d'authentification, pour que quelqu'un
            // qui gere deux comptes les distingue.
            promesse.current = setupTotp(accountName);
        }
        promesse.current
            .then(setSetup)
            .catch((failure: unknown) =>
                setBroken(failure instanceof Error ? `${failure.name}: ${failure.message}` : String(failure))
            );
    }, []);

    const verify = () => {
        if (busy || code.length < TOTP_CODE_LENGTH) {
            return;
        }
        setBusy(true);
        setError('');
        verifyTotpSetup(code)
            .then(async () => {
                // L'EVENEMENT, pas l'etat : le back-office lit l'etat dans le pool, et
                // ce que le pool ne sait pas dire est le moment de la mise en conformite.
                // N'attend rien et n'echoue pas visiblement, le facteur etant pose quoi
                // qu'il arrive ici.
                const token = await getAuthorizationToken();
                if (token) {
                    recordMfaEnrolment(token);
                }
                // No « c'est fait » screen: the person was on their way somewhere when
                // this interrupted them, and the kind thing is to resume, not to make
                // them acknowledge a step they did not ask for.
                onDone();
            })
            .catch(() => {
                setError(c('Mfa error').t`Invalid code.`);
            })
            .finally(() => setBusy(false));
    };

    return (
        /**
         * UNE MODALE, ET NON UNE PAGE, arbitre par Mohamed sur l'ecran : la maquette V3
         * dessine cet appairage en modale (`o-appairage`) et c'est ce que le lecteur
         * reconnait. Elle se pose sur la page de redirection, donc sur un fond neutre :
         * il n'y a rien derriere qu'on ferait miroiter.
         *
         * **`dismissable={false}` est ce qui la distingue de celle du profil.** La-bas
         * poser un facteur est un CHOIX, avec croix et « Annuler » ; ici c'est une
         * CONDITION d'entree. Le drapeau neutralise aussi Echap et le clic sur le fond :
         * masquer la croix seule donnerait une modale qui a l'air scellee et s'ouvre a la
         * premiere touche, ce qui est pire que les deux.
         *
         * La sortie est A L'INTERIEUR, « Se deconnecter » : l'alternative a poser un
         * facteur est de partir, jamais d'etre enferme dans un dialogue.
         */
        <>
            {/* LE DECOR PUBLIC RESTE DERRIERE LA MODALE, et c'est ce que fait deja l'ecran
                de connexion avec son defi MFA : `LoginPage` garde `PublicLayout` et pose la
                modale a cote. Sans lui, la modale flottait sur du vide, ce qui se lit comme
                une page qui a fini de se charger sans rien afficher.

                Aucun enfant : la modale porte tout, et le decor est precisement ce qui doit
                rester visible. Le logo est donc rendu ici, par le decor, et non dans la
                modale ou il ferait doublon. */}
            <PublicLayout>{null}</PublicLayout>
            <Modal
                open
                dismissable={false}
                // Jamais appele : `dismissable` neutralise les deux chemins de fermeture.
                // Le passer vide plutot que d'inventer une action garde le contrat du
                // composant lisible.
                onClose={() => undefined}
                title={c('Mfa setup title').t`Set up two-factor authentication`}
                size={600}
                panelProps={{ className: 'mfa-modal' }}
            >
                <div className="flex flex-col gap-4">
                    {/* Pourquoi la personne est la, dit avant quoi faire : une barriere sans
                    raison se lit comme une panne, et le premier reflexe est de recharger. */}
                    <p className="text-sm">
                        {c('Mfa setup')
                            .t`Your organisation requires two-factor authentication. Set up an authenticator app to carry on: from now on, a code from that app will be asked for at every sign-in.`}
                    </p>

                    {/* LE CADRE NE BASCULE PAS. Rendre un chargement pleine page puis la
                    modale ferait sauter l'ecran sous les yeux de la personne, pour une
                    attente d'une seconde. La modale s'affiche tout de suite, et c'est son
                    CONTENU qui change. */}
                    {setup === null && broken === null ? (
                        /*
                         * LE QR ET LA CLE SE DESSINENT, a leur taille : le code fait 158 px
                         * de cote, la cle une ligne sous lui. Un rond centre laisse la modale
                         * a une hauteur, puis elle grandit d'un coup quand le secret arrive,
                         * et c'est exactement le saut que le commentaire au-dessus evite.
                         */
                        <div className="flex flex-col items-center gap-3 py-6" aria-busy="true">
                            <SkeletonLoader width={158} height={158} />
                            <SkeletonLoader width={220} height={16} />
                            <SkeletonLoader width={180} height={12} />
                        </div>
                    ) : broken !== null ? (
                        <>
                            <p className="text-sm text-danger" role="alert">
                                {c('Mfa setup')
                                    .t`The setup could not be started. This is not something you can fix from here: try again in a moment, and tell your Freelance.com contact if it persists.`}
                            </p>
                            {/* Le message technique, tel quel : c'est ce que le support
                            demandera, et le paraphraser le rendrait inutilisable. */}
                            {broken && <p className="text-xs text-text-hint break-words">{broken}</p>}
                            <Button category="weak" onClick={() => window.location.reload()}>
                                {c('Action').t`Try again`}
                            </Button>
                        </>
                    ) : (
                        setup && (
                            <form
                                name="mfa-setup-form"
                                method="post"
                                onSubmit={(event) => {
                                    event.preventDefault();
                                    verify();
                                }}
                                className="flex flex-col gap-4"
                            >
                                {/* Les deux etapes viennent du composant partage : le profil
                                rend exactement les memes, dans une modale fermable. */}
                                <TotpPairing
                                    uri={setup.uri}
                                    secret={setup.secret}
                                    code={code}
                                    onCode={setCode}
                                    error={error}
                                    busy={busy}
                                />
                                <Button type="submit" size="large" fullWidth loading={busy}>
                                    {c('Mfa setup action').t`Confirm and continue`}
                                </Button>
                            </form>
                        )
                    )}

                    <Button category="ghost" onClick={() => logout()}>
                        {c('Action').t`Sign out`}
                    </Button>
                </div>
            </Modal>
        </>
    );
};

export default MfaSetupPage;
